import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Play, Square } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { HoldsCountInput } from './HoldsCountInput';
import { LeadTimeInput } from './LeadTimeInput';
import { LeadCardEditor, type LeadCardDraft } from './LeadCardEditor';
import { LeadCardSummary } from './LeadCardSummary';
import { LeadStyleSwitch } from './LeadStyleSwitch';
import type { LeadClimbStyle } from './lead-climb-style';
import { RouteHoldsCanvas } from './route-holds/RouteHoldsCanvas';
import { useRouteHolds } from './route-holds/useRouteHolds';
import { formatLeadTime } from './lead-time';
import type { RouteHold } from '../../types/competition-round';

/**
 * How often the big clock redraws. The thousandths digit therefore steps by about
 * this much rather than counting every one of them — a thumb on a button is
 * nowhere near that precise, and the number that matters is the one the clock
 * hands over when it stops, which is exact to the millisecond.
 */
const TICK_MS = 30;

/** Where the dialog is: before the climb, on it, or writing the card. */
type Phase = 'idle' | 'running' | 'review';

interface LeadScoringDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whose card this is — the heading. */
  athleteName: string;
  /** The route being climbed, and what is known about it. */
  routeName: string;
  imageUrl?: string | null;
  holds?: RouteHold[];
  /** The holds counted on the route: the ceiling the inputs take. */
  maxHolds?: number | null;
  /** The card as it stands. Both null on a route this athlete has not climbed. */
  cardHolds: string | null;
  cardTimeSeconds: number | null;
  /** The server's own formatting of that time, where there is one. */
  cardFormattedTime?: string | null;
  /** The style on the card as it stands, where there is one. */
  cardClimbedAs?: LeadClimbStyle | null;
  /**
   * The styles this athlete may climb this route in (`permittedLeadStyles`).
   * Two of them put the switch on the review screen and hold the save back
   * until one is pressed; one is shown as a label.
   */
  styles?: LeadClimbStyle[];
  saving?: boolean;
  /** Nothing may be written — the round is closed, or this is not the viewer's card. */
  readOnly?: boolean;
  onSave: (draft: LeadCardDraft) => void | Promise<void>;
  /** Takes the card off the route. Absent where there is nothing to take off. */
  onClear?: () => void | Promise<void>;
}

/**
 * One athlete, one lead route, from the ground to the card.
 *
 * This is what a judge and a squad's team manager hold while somebody climbs, and
 * it is shaped like the job rather than like the data. Three screens, in the order
 * the afternoon happens:
 *
 * 1. **Before the climb** — whose card it is, which route, whatever is already on
 *    it (editable, with its own save, for a correction made after the fact), and
 *    one large Start.
 * 2. **On the climb** — the clock, and nothing else but a large red Stop. Nothing
 *    to read and nothing to mis-tap while watching a climber.
 * 3. **The card** — the route's photo with its holds circled, and under it the two
 *    numbers: the hold reached, and the time the clock just took. **Tapping a
 *    circle writes its number into the holds field**, which is the whole point of
 *    having drawn the route: the judge looks up, sees the climber on the circled
 *    hold, and taps it. On a class that may top rope or lead, the style is asked
 *    here too, and the save waits for it.
 *
 * Saving closes the dialog: one athlete at a time, and the table behind it is
 * where the next one is chosen.
 */
export function LeadScoringDialog({
  open,
  onOpenChange,
  athleteName,
  routeName,
  imageUrl,
  holds = [],
  maxHolds,
  cardHolds,
  cardTimeSeconds,
  cardFormattedTime,
  cardClimbedAs = null,
  styles = ['lead'],
  saving = false,
  readOnly = false,
  onSave,
  onClear,
}: LeadScoringDialogProps) {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>('idle');
  /** Milliseconds the clock has counted, while it is running or just stopped. */
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);

  /**
   * The card being written on the review screen. The style starts as the one
   * the route leaves open, or as nothing at all where there are two: a card is
   * never born carrying a guess between top rope and lead.
   */
  const onlyStyle = styles.length === 1 ? styles[0] : null;
  const [draft, setDraft] = useState<LeadCardDraft>({
    holds: '',
    timeSeconds: null,
    style: onlyStyle,
  });

  // Every opening starts from the top, done while rendering so no stale clock or
  // half-written card survives being closed and reopened.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPhase('idle');
      setElapsed(0);
      setDraft({ holds: '', timeSeconds: null, style: cardClimbedAs ?? onlyStyle });
    }
  }

  useEffect(() => {
    if (phase !== 'running') return;

    const timer = window.setInterval(() => {
      setElapsed(Date.now() - startedAt.current);
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, [phase]);

  const start = () => {
    startedAt.current = Date.now();
    setElapsed(0);
    setPhase('running');
  };

  /**
   * The clock stops and hands over what it counted. The time is a draft from here
   * — the judge's thumb is not a photofinish — and the holds are still to be said.
   */
  const stop = () => {
    const counted = Date.now() - startedAt.current;
    setElapsed(counted);
    setDraft({
      holds: cardHolds ?? '',
      timeSeconds: counted / 1000,
      style: cardClimbedAs ?? onlyStyle,
    });
    setPhase('review');
  };

  const complete = draft.holds !== '' && draft.timeSeconds !== null && draft.style != null;
  const drawn = !!imageUrl && holds.length > 0;

  /**
   * The photo on the review screen: the same surface the admin drew on, read-only,
   * with a tap on a circle writing its number into the field below.
   */
  const picker = useRouteHolds({ holds, onChange: () => {}, readOnly: true });
  const pickedNumber = draft.holds === '' ? null : Number.parseInt(draft.holds, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92svh] overflow-y-auto sm:max-w-lg"
        data-testid="lead-scoring-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="scoring-athlete">{athleteName}</DialogTitle>
          <DialogDescription data-testid="scoring-route">{routeName}</DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <div className="space-y-6">
            {/* Whatever is already on the card, and the way to put it right. A
                correction is made here rather than by running the clock again. */}
            {readOnly ? (
              <div className="flex items-center justify-center rounded-lg border border-dashed p-4">
                <LeadCardSummary
                  holds={cardHolds}
                  timeSeconds={cardTimeSeconds}
                  formattedTime={cardFormattedTime}
                  climbedAs={cardClimbedAs}
                />
              </div>
            ) : (
              <div className="space-y-2 rounded-lg border p-3">
                <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {cardHolds == null
                    ? t('leadRounds.scoring.noCardYet')
                    : t('leadRounds.scoring.previousCard')}
                </span>
                <LeadCardEditor
                  holds={cardHolds}
                  timeSeconds={cardTimeSeconds}
                  climbedAs={cardClimbedAs}
                  styles={styles}
                  maxHolds={maxHolds}
                  saving={saving}
                  onSave={onSave}
                  onClear={onClear}
                  data-testid="scoring-previous-card"
                />
              </div>
            )}

            {!readOnly && (
              <div className="flex justify-center py-2">
                <Button
                  size="lg"
                  className="h-28 w-28 flex-col gap-1 rounded-full text-base font-semibold"
                  onClick={start}
                  data-testid="scoring-start"
                >
                  <Play className="size-7" aria-hidden />
                  {t('leadRounds.scoring.start')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* On the climb: the clock, and one thing to press. Nothing else is on
            screen to be read or mis-tapped while watching a climber. */}
        {phase === 'running' && (
          <div className="flex flex-col items-center gap-6 py-4">
            <span
              className="font-mono text-5xl font-bold tabular-nums"
              data-testid="scoring-clock"
              aria-live="off"
            >
              {formatLeadTime(elapsed / 1000)}
            </span>

            <Button
              size="lg"
              variant="destructive"
              className="h-28 w-28 flex-col gap-1 rounded-full text-base font-semibold"
              onClick={stop}
              data-testid="scoring-stop"
            >
              <Square className="size-7" aria-hidden />
              {t('leadRounds.scoring.stop')}
            </Button>
          </div>
        )}

        {phase === 'review' && (
          <div className="space-y-4">
            {/* The route as it was drawn. A tap on a circle is the hold reached —
                which is why the admin circled it in the first place. */}
            {drawn ? (
              <RouteHoldsCanvas
                editor={picker}
                imageUrl={imageUrl!}
                holdLabel={(number) => t('leadRounds.scoring.pickHold', { number })}
                resetZoomLabel={t('leadRounds.draw.resetZoom')}
                onPick={(number) =>
                  setDraft((current) => ({ ...current, holds: String(number) }))
                }
                picked={pickedNumber}
                testId="scoring-canvas"
                className="max-h-[45svh]"
              />
            ) : (
              <p className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                {t('leadRounds.scoring.notDrawn')}
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('leadRounds.holds')}
                </span>
                <HoldsCountInput
                  value={draft.holds}
                  max={maxHolds}
                  onChange={(value) => setDraft((current) => ({ ...current, holds: value }))}
                  data-testid="scoring-holds"
                />
              </div>

              <div className="space-y-1">
                <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('leadRounds.time')}
                </span>
                {/* Prefilled with what the clock took, and still a draft: the
                    judge corrects it before saving. */}
                <LeadTimeInput
                  value={draft.timeSeconds}
                  onChange={(value) => setDraft((current) => ({ ...current, timeSeconds: value }))}
                  data-testid="scoring-time"
                />
              </div>
            </div>

            {/* Top rope or lead. Last of the three because it is the one the
                judge knows before the climber leaves the ground — and the save
                waits for it, since a led route beats a top roped one whatever
                the numbers above say. */}
            <div className="space-y-1">
              <span className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('leadRounds.style.label')}
              </span>
              <LeadStyleSwitch
                styles={styles}
                value={draft.style ?? null}
                onChange={(style) => setDraft((current) => ({ ...current, style }))}
                data-testid="scoring-style"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                onClick={() => setPhase('idle')}
                disabled={saving}
                data-testid="scoring-back"
              >
                {t('common.cancel')}
              </Button>
              <Button
                className={cn('min-w-32', !complete && 'opacity-60')}
                disabled={saving || !complete}
                onClick={() => onSave(draft)}
                data-testid="scoring-save"
              >
                {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
                {t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
